import React, { useEffect, useRef, useState } from "react";
import { translate } from "../app";
import { translations } from "../translations";
import { api, AgentServiceFilters } from "../api";
import { filterServicesByAgentFilters, buildAgentServiceResults } from "../agent-service-filters";
import { MapCompanyCards } from "./map-company-cards";
import { AiDisclaimerNote } from "./ai-disclaimer-note";
import {
  trackAgentFiltersApplied,
  trackAgentResultCategories,
  trackAiChatMessage,
  trackZeroResults,
} from "../map-analytics";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  services?: Service[];
  isGreeting?: boolean;
  dir?: "rtl" | "ltr";
};

function getOrCreateUid(): string {
  const key = "signpost-ai-uid";
  let uid = localStorage.getItem(key);
  if (!uid) {
    uid = crypto.randomUUID();
    localStorage.setItem(key, uid);
  }
  return uid;
}

const CHAT_HISTORY_KEY = "signpost-ai-history";
const DIR_TAG_PATTERN = /<dir:(rtl|ltr)>\s*$/i;

function parseMessageDirection(content: string): {
  text: string;
  dir?: "rtl" | "ltr";
} {
  const match = content.match(DIR_TAG_PATTERN);
  if (!match) {
    return { text: content };
  }

  const dir = match[1].toLowerCase() as "rtl" | "ltr";
  const text = content.replace(DIR_TAG_PATTERN, "").trimEnd();
  return { text, dir };
}

function getGreeting(): ChatMessage {
  return {
    role: "assistant",
    content: translate(translations.aiGreeting),
    isGreeting: true,
  };
}

function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(CHAT_HISTORY_KEY);
    if (!raw) return [getGreeting()];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((msg: ChatMessage, index: number) =>
        index === 0 && msg.role === "assistant"
          ? { ...msg, isGreeting: true }
          : msg,
      );
    }
  } catch {
  }
  return [getGreeting()];
}

function normalizeAssistantContent(content: string): string {
  return content.replace(/\\n/g, "\n");
}

function renderAssistantContent(content: string) {
  const normalized = normalizeAssistantContent(content);
  const parts = normalized.split(/(\*\*[^*]+?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return part;
  });
}

interface ChatbotPanelProps {
  initialMessage?: string | null;
  onMessageConsumed?: () => void;
  onChatResponse?: () => void;
  onCardClick?: (service: Service) => void;
  onServicesFound?: (services: Service[], filters?: AgentServiceFilters) => void;
  allServices?: Service[];
  userLocation?: { latitude: number; longitude: number } | null;
  /** Fill the parent height (e.g. inside the mobile full-screen modal) instead
   *  of using a fixed card height. Also drops the card chrome. */
  fill?: boolean;
}

export function ChatbotPanel({
  initialMessage,
  onMessageConsumed,
  onChatResponse,
  onCardClick,
  onServicesFound,
  allServices = [],
  userLocation = null,
  fill = false,
}: ChatbotPanelProps) {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>(loadStoredMessages);
  const [isLoading, setIsLoading] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const userLocationRef = useRef(userLocation);

  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  // Persist the conversation so it survives page navigation within the tab.
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(messages));
    } catch {
    }
  }, [messages]);

  const resetConversation = () => {
    setMessages([getGreeting()]);
    try {
      sessionStorage.removeItem(CHAT_HISTORY_KEY);
    } catch {
    }
  };

  const scrollToBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return;

    // Jump instantly to the bottom. Smooth scrolling here fought with the
    // rapid loading-dots -> message swap and produced visible stutter.
    el.scrollTop = el.scrollHeight;
  };

  useEffect(scrollToBottom, [messages, isLoading]);
  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    const exchangeId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    trackAiChatMessage({
      role: "user",
      text,
      exchangeId,
    });

    const userMessage: ChatMessage = {
      role: "user",
      content: text,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const uid = getOrCreateUid();
    const result = await api.askSignpostAI(
      uid,
      text,
      userLocationRef.current ?? undefined,
    );

    let matchedServices: Service[] = [];
    let messageContent = result?.response || "Sorry, something went wrong.";

    if (result?.filters && allServices.length > 0) {
      matchedServices = filterServicesByAgentFilters(allServices, result.filters, {
        translateName: translate,
      });

      const followUp = await api.sendSignpostAIServiceResults(uid, {
        serviceCount: matchedServices.length,
        language: result.language,
        services: buildAgentServiceResults(matchedServices, translate, 5),
      });

      messageContent =
        followUp.response ||
        (matchedServices.length > 0
          ? `Found ${matchedServices.length} services matching your search.`
          : "No services matched your search. Try adjusting your question.");
    } else if (result?.references && allServices.length > 0) {
      const seen = new Set<number>();
      result.references.forEach((ref) => {
        const serviceIdMatch = ref.link?.match(/\/services\/(\d+)/);
        if (!serviceIdMatch) return;
        const serviceId = parseInt(serviceIdMatch[1], 10);
        if (seen.has(serviceId)) return;
        seen.add(serviceId);
        const service = allServices.find((s) => s.id === serviceId);
        if (service) matchedServices.push(service);
      });
    }

    const { text: displayContent, dir } = parseMessageDirection(messageContent);

    trackAiChatMessage({
      role: "assistant",
      text: displayContent,
      exchangeId,
      resultCount: matchedServices.length,
      language: result?.language,
    });

    const aiMessage: ChatMessage = {
      role: "assistant",
      content: displayContent,
      services: matchedServices.length > 0 ? matchedServices : undefined,
      dir,
    };

    setMessages((prev) => [...prev, aiMessage]);
    setIsLoading(false);

    if (result?.filters) {
      trackAgentFiltersApplied({
        filters: result.filters,
        resultCount: matchedServices.length,
        searchTerm: text,
      });

      if (matchedServices.length === 0) {
        trackZeroResults({
          searchType: "ai_chat",
          searchTerm: text,
          activeFilters: JSON.stringify(result.filters),
        });
      }
    }

    if (matchedServices.length > 0) {
      trackAgentResultCategories({
        services: matchedServices,
        searchTerm: text,
      });
    }

    if (result?.filters) {
      onServicesFound?.(matchedServices, result.filters);
    } else if (matchedServices.length > 0) {
      onServicesFound?.(matchedServices);
    }

    onChatResponse?.();
  };

  /**
   * Auto-send injected message from Map
   */
  const hasAutoSentRef = useRef(false);

  useEffect(() => {
    if (initialMessage && !hasAutoSentRef.current) {
      hasAutoSentRef.current = true;
      sendMessage(initialMessage);
      onMessageConsumed?.();
    }
  }, [initialMessage]);

  const handleAsk = () => {
    if (!question.trim()) return;
    sendMessage(question);
    setQuestion("");
  };

  return (
    <div
      className={`chat-panel flex flex-col w-full max-w-full min-w-0 overflow-hidden ${
        fill ? "chat-panel--fill h-full flex-1" : "h-[700px] md:h-[80vh]"
      }`}
    >
      {messages.length > 1 && (
        <div className="chat-header">
          <button type="button" className="chat-newchat" onClick={resetConversation}>
            {translate(translations.newChat)}
          </button>
        </div>
      )}
      {/* Messages */}
      <div
        ref={messagesContainerRef}
        role="log"
        aria-live="polite"
        className="chat-scroll flex-1 min-h-0 min-w-0 overflow-y-auto custom-scrollbar space-y-3 px-3 py-4"
        style={{
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
        }}
      >
        <style>
          {`
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }

      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }

      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #e5e7eb;
        border-radius: 9999px;
      }

      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: #d1d5db;
      }

      .custom-scrollbar::-webkit-scrollbar-button {
        display: none;
      }

      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: #e5e7eb transparent;
      }
    `}
        </style>
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`chat-msg w-full flex flex-col ${
              msg.role === "user" ? "items-end" : "items-start"
            }`}
          >
            {msg.content && (
              <div
                className={`chat-bubble ${
                  msg.role === "user" ? "chat-bubble--user" : "chat-bubble--bot"
                }`}
                dir={msg.dir}
                style={
                  msg.dir
                    ? {
                        direction: msg.dir,
                        textAlign: msg.dir === "rtl" ? "right" : "left",
                        unicodeBidi: "isolate",
                      }
                    : undefined
                }
              >
                {msg.isGreeting ? (
                  <div className="whitespace-pre-wrap">
                    {translate(translations.aiGreeting)}
                  </div>
                ) : msg.role === "assistant" ? (
                  <div className="whitespace-pre-wrap">
                    {renderAssistantContent(msg.content)}
                  </div>
                ) : (
                  msg.content
                )}
              </div>
            )}
            {msg.services && msg.services.length > 0 && (
              <div className="w-full mt-2">
                <MapCompanyCards
                  services={msg.services}
                  onCardClick={onCardClick}
                  layout="vertical"
                />
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div
            role="status"
            aria-label={translate(translations.loading)}
            className="chat-msg chat-bubble chat-bubble--bot mr-auto flex items-center gap-1"
          >
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
              style={{ animationDelay: "0ms" }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
              style={{ animationDelay: "150ms" }}
            />
            <span
              className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
              style={{ animationDelay: "300ms" }}
            />
          </div>
        )}
      </div>

      <AiDisclaimerNote />

      {/* Input */}
      <form
        className="chat-input-row"
        onSubmit={(e) => {
          e.preventDefault();
          handleAsk();
        }}
      >
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={translate(translations.aiPlaceholder)}
          aria-label={translate(translations.aiPlaceholder)}
          className="chat-input"
        />
        <button
          type="submit"
          className="chat-send"
          aria-label={translate(translations.askAI)}
          disabled={!question.trim() || isLoading}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2 .4 6.4Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </form>
    </div>
  );
}
